import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { ArrowLeft, Save, Loader2 } from "lucide-react";

export default function AdminActivityEdit() {
  const navigate = useNavigate();
  const { id } = useParams();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    title: "",
    description: "",
    activity_date: "",
    activity_time: "",
    location: "",
    registration_deadline: "",
  });

  // ==========================================
  // Charger l'activité
  // ==========================================
  useEffect(() => {
    async function loadActivity() {
      try {
        setLoading(true);

        const res = await fetch("/api/activity?action=getAll");
        const data = await res.json();

        if (!res.ok || !data.success) {
          throw new Error(
            data.message || "Impossible de récupérer les activités."
          );
        }

        const activity = (data.activities || []).find(
          (item) => String(item.id) === String(id)
        );

        if (!activity) {
          toast.error("Activité introuvable.");
          navigate("/admin/activities");
          return;
        }

        setForm({
          title: activity.title || "",
          description: activity.description || "",
          activity_date: activity.activity_date || "",
          activity_time: activity.activity_time
            ? activity.activity_time.slice(0, 5)
            : "",
          location: activity.location || "",
          registration_deadline: formatDeadlineForInput(
            activity.registration_deadline
          ),
        });
      } catch (error) {
        console.error("LOAD ACTIVITY ERROR:", error);
        toast.error(error.message || "Erreur lors du chargement.");
      } finally {
        setLoading(false);
      }
    }

    if (id) {
      loadActivity();
    }
  }, [id, navigate]);

  // ==========================================
  // Convertir deadline pour datetime-local
  // ==========================================
  function formatDeadlineForInput(deadline) {
    if (!deadline) return "";

    const date = new Date(deadline);

    if (Number.isNaN(date.getTime())) {
      return "";
    }

    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }

  // ==========================================
  // Change
  // ==========================================
  function handleChange(e) {
    const { name, value } = e.target;

    setForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  }

  // ==========================================
  // Modifier
  // ==========================================
  async function handleSubmit(e) {
    e.preventDefault();

    if (saving) return;

    if (
      !form.title.trim() ||
      !form.description.trim() ||
      !form.activity_date ||
      !form.activity_time ||
      !form.location.trim() ||
      !form.registration_deadline
    ) {
      toast.error("Veuillez remplir tous les champs.");
      return;
    }

    setSaving(true);

    try {
      /*
       * datetime-local représente l'heure locale.
       *
       * On crée volontairement la Date à partir
       * des composants locaux pour éviter le problème
       * de +1 heure à l'affichage.
       */
      const deadlineDate = new Date(form.registration_deadline);

      if (Number.isNaN(deadlineDate.getTime())) {
        toast.error("Date limite invalide.");
        return;
      }

      const registration_deadline = deadlineDate.toISOString();

      const res = await fetch("/api/activity?action=update", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id,

          title: form.title.trim(),
          description: form.description.trim(),

          activity_date: form.activity_date,
          activity_time: form.activity_time,

          location: form.location.trim(),

          registration_deadline,
        }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        toast.error(
          data.message || "Impossible de modifier l'activité."
        );
        return;
      }

      toast.success("Activité modifiée avec succès !");

      setTimeout(() => {
        navigate("/admin/activities");
      }, 500);
    } catch (error) {
      console.error("UPDATE ACTIVITY ERROR:", error);
      toast.error(error.message || "Erreur serveur.");
    } finally {
      setSaving(false);
    }
  }

  // ==========================================
  // Loading
  // ==========================================
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 dark:bg-slate-950 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <Loader2
            size={35}
            className="animate-spin text-indigo-600"
          />

          <p className="text-sm text-slate-500 dark:text-slate-400">
            Chargement de l'activité...
          </p>
        </div>
      </div>
    );
  }

  // ==========================================
  // Page
  // ==========================================
  return (
    <div className="min-h-screen bg-slate-100 dark:bg-slate-950 p-4 sm:p-6 lg:p-8">
      <div className="max-w-4xl mx-auto">

        {/* HEADER */}
        <div className="mb-8">
          <button
            type="button"
            onClick={() => navigate("/admin/activities")}
            className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-indigo-600 transition mb-4"
          >
            <ArrowLeft size={17} />
            Retour aux activités
          </button>

          <h1 className="text-3xl font-extrabold text-slate-900 dark:text-white">
            Modifier l'activité
          </h1>

          <p className="mt-1 text-slate-500 dark:text-slate-400">
            Modifiez les informations de cette activité.
          </p>
        </div>

        {/* FORM */}
        <form
          onSubmit={handleSubmit}
          className="bg-white dark:bg-slate-900 rounded-3xl shadow-xl border border-slate-200 dark:border-slate-800 p-6 sm:p-8"
        >
          <div className="space-y-6">

            {/* TITLE */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">
                Titre de l'activité
              </label>

              <input
                type="text"
                name="title"
                value={form.title}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Ex: Atelier Leadership"
              />
            </div>

            {/* DESCRIPTION */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">
                Description
              </label>

              <textarea
                name="description"
                value={form.description}
                onChange={handleChange}
                rows={5}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500 resize-none"
                placeholder="Description de l'activité..."
              />
            </div>

            {/* DATE + TIME */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">

              <div>
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">
                  Date de l'activité
                </label>

                <input
                  type="date"
                  name="activity_date"
                  value={form.activity_date}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">
                  Heure de l'activité
                </label>

                <input
                  type="time"
                  name="activity_time"
                  value={form.activity_time}
                  onChange={handleChange}
                  className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            </div>

            {/* LOCATION */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">
                Lieu
              </label>

              <input
                type="text"
                name="location"
                value={form.location}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
                placeholder="Ex: Rabat"
              />
            </div>

            {/* DEADLINE */}
            <div>
              <label className="block text-sm font-semibold text-slate-700 dark:text-slate-200 mb-2">
                Date limite des inscriptions
              </label>

              <input
                type="datetime-local"
                name="registration_deadline"
                value={form.registration_deadline}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-slate-900 dark:text-white outline-none focus:ring-2 focus:ring-indigo-500"
              />

              <p className="mt-2 text-xs text-slate-400">
                La date limite doit être avant la date de l'activité.
              </p>
            </div>

            {/* ACTIONS */}
            <div className="flex flex-col sm:flex-row gap-3 pt-4 border-t border-slate-100 dark:border-slate-800">

              <button
                type="button"
                onClick={() => navigate("/admin/activities")}
                className="w-full sm:w-auto px-6 py-3 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-200 font-semibold hover:bg-slate-50 dark:hover:bg-slate-800 transition"
              >
                Annuler
              </button>

              <button
                type="submit"
                disabled={saving}
                className="w-full sm:flex-1 inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold shadow-md transition"
              >
                {saving ? (
                  <>
                    <Loader2 size={18} className="animate-spin" />
                    Enregistrement...
                  </>
                ) : (
                  <>
                    <Save size={18} />
                    Enregistrer les modifications
                  </>
                )}
              </button>

            </div>
          </div>
        </form>
      </div>
    </div>
  );
}